import type { Camp } from '@orbomb/content';
import type { SimConfig } from './config';
import { SIMULATION_VERSION } from './constants';
import { EventSink } from './events';
import {
  pruneExplosions,
  resolveAutoArming,
  resolveContactArming,
  resolveDetonations,
  resolveExplosionTargets,
} from './gameplay/bombs';
import { applyDamage } from './gameplay/damage';
import { applyForfeit, beginTick, evaluateEnd } from './gameplay/match';
import { resolveBonuses } from './gameplay/bonuses';
import { resolveRespawns } from './gameplay/respawn';
import { normalizeInput, stepPhysics } from './physics';
import { nextDiagonalDirection, seedToState } from './rng';
import type {
  GoldenBallState,
  InputVector,
  MatchState,
  PlayerBallState,
  StepResult,
  TickInputs,
  TotemState,
} from './types';

export interface CreateMatchOptions {
  seed: number | string;
  /** Countdown length in ticks. Defaults to the first-match countdown (3 s). */
  countdownTicks?: number;
}

function newPlayer(camp: Camp, config: SimConfig): PlayerBallState {
  const spawn = config.map.playerSpawns[camp];
  return {
    id: camp === 'ORANGE' ? 'player-orange' : 'player-turquoise',
    camp,
    x: spawn.x,
    y: spawn.y,
    vx: 0,
    vy: 0,
    status: 'NORMAL',
    stunUntilTick: 0,
    protectedUntilTick: 0,
  };
}

function newTotem(camp: Camp, config: SimConfig): TotemState {
  return {
    id: camp === 'ORANGE' ? 'totem-orange' : 'totem-turquoise',
    camp,
    hp: config.totemHp,
    destroyedAtTick: null,
  };
}

/** Initial golden balls: point-symmetric pairs receive opposite velocities (fairness). */
function initialGoldens(config: SimConfig, rng: { rng: number }): GoldenBallState[] {
  const spawns = config.map.goldenSpawns.slice(0, config.golden.count);
  const velocities: ({ x: number; y: number } | null)[] = spawns.map(() => null);
  spawns.forEach((cell, i) => {
    if (velocities[i]) return;
    const dir = nextDiagonalDirection(rng, config.golden.minAxisAngleDegrees);
    velocities[i] = { x: dir.x * config.golden.cruiseSpeed, y: dir.y * config.golden.cruiseSpeed };
    const twin = spawns.findIndex(
      (c, j) =>
        j !== i && c.col === config.columns - 1 - cell.col && c.row === config.rows - 1 - cell.row,
    );
    if (twin >= 0 && !velocities[twin]) {
      const v = velocities[i] as { x: number; y: number };
      velocities[twin] = { x: -v.x, y: -v.y };
    }
  });
  return spawns.map((cell, i) => ({
    id: `golden-${i}`,
    status: 'NEUTRAL',
    x: cell.col + 0.5,
    y: cell.row + 0.5,
    vx: velocities[i]?.x ?? 0,
    vy: velocities[i]?.y ?? 0,
    armedAtTick: null,
    detonationTick: null,
    firstToucher: null,
    lastToucher: null,
    armedBy: null,
    chainedByExplosionId: null,
    respawnAtTick: null,
    activeAtTick: null,
  }));
}

export function createMatch(config: SimConfig, options: CreateMatchOptions): MatchState {
  const seed = seedToState(options.seed);
  const rng = { rng: seed };
  const countdown = options.countdownTicks ?? config.ticks.countdown;
  const goldens = initialGoldens(config, rng);
  return {
    simulationVersion: SIMULATION_VERSION,
    contentHash: config.contentHash,
    balanceHash: config.balanceHash,
    mapId: config.map.id,
    seed,
    tick: 0,
    rng: rng.rng,
    players: [newPlayer('ORANGE', config), newPlayer('TURQUOISE', config)],
    goldens,
    totems: [newTotem('ORANGE', config), newTotem('TURQUOISE', config)],
    explosions: [],
    nextExplosionId: 1,
    bonuses: [],
    nextBonusId: 1,
    clock: {
      phase: countdown > 0 ? 'COUNTDOWN' : 'REGULATION',
      countdownEndTick: countdown,
      regulationEndTick: countdown + config.ticks.regulation,
      suddenDeathStartTick: null,
      nextAutoArmTick: null,
      nextBonusTick: countdown + config.ticks.bonusInterval,
    },
    spawnRegion: 0,
    stats: {
      armings: 0,
      autoArmings: 0,
      explosions: 0,
      chainDetonations: 0,
      playerHits: 0,
      totemHits: 0,
      selfHits: 0,
      firstArmTick: null,
      firstPlayerHitTick: null,
      firstTotemHitTick: null,
    },
    result: null,
  };
}

export const NEUTRAL_INPUTS: TickInputs = Object.freeze({
  ORANGE: Object.freeze({ x: 0, y: 0 }),
  TURQUOISE: Object.freeze({ x: 0, y: 0 }),
});

/**
 * Advances the match by exactly one 60 Hz tick, mutating `state` in place.
 * Resolution order follows docs/architecture.md section 11 (see docs/decisions.md ADR-005).
 * Once the match has ended, further calls are no-ops.
 */
export function step(state: MatchState, config: SimConfig, inputs: TickInputs): StepResult {
  if (state.result) return { state, events: [] };
  const sink = new EventSink(state.tick);

  // 1. Clock phases and stun/protection timers.
  beginTick(state, config, sink);

  // 2. Inputs (validated and bounded). Countdown and stun freeze the ball entirely.
  const countdown = state.clock.phase === 'COUNTDOWN';
  const frozen = [
    countdown || state.players[0].status === 'STUNNED',
    countdown || state.players[1].status === 'STUNNED',
  ] as const;
  const normalised: readonly [InputVector, InputVector] = [
    normalizeInput(inputs.ORANGE),
    normalizeInput(inputs.TURQUOISE),
  ];

  // 3-9. Forces, integration, substepped collisions.
  const contacts = new Uint8Array(2 * state.goldens.length);
  stepPhysics(state, config, { frozen, inputs: normalised }, contacts, sink);

  // 10. Contact arming, then sudden-death automatic arming.
  resolveContactArming(state, config, contacts, frozen, sink);
  resolveAutoArming(state, config, sink);

  // 11-12. Scheduled detonations create explosions.
  resolveDetonations(state, config, sink);

  // 13-15. Explosion targets: chains, neutral ejections, pending damage (deduplicated).
  const pending = resolveExplosionTargets(state, config, sink);

  // 16-17. Stun / lives, applied in stable order.
  applyDamage(state, config, pending, sink);

  // 18. Respawns and materialisations.
  resolveRespawns(state, config, sink);

  // 18b. Bonuses: pickups, expiry, then the next appearance.
  resolveBonuses(state, config, sink);

  // 19. Expire finished explosion visuals.
  pruneExplosions(state, sink.tick + 1);

  // 21. Match end, only after the whole tick has been applied.
  evaluateEnd(state, config, sink);

  state.tick += 1;
  return { state, events: sink.events };
}

/** Ends the match immediately in favour of the other camp (reconnection timeout). */
export function forfeit(state: MatchState, loser: Camp): StepResult {
  const sink = new EventSink(state.tick);
  applyForfeit(state, loser, sink);
  return { state, events: sink.events };
}
